import React from 'react'
import { Skeleton } from 'antd'
import { Well } from '@pushly/aqe/lib/components'
import TemplatizedTextField from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { getClassNames } from '../../../_utils/classnames'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { DomainDto } from '../../../dtos/domain'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { TemplateSelector } from '../../template-selector/template-selector'

type AppMessageBasicsBuilderProps = {
    domain: DomainDto
    /** Set when the message targets a send integration that is currently off. */
    channelTargetDisabled?: DeliveryChannel
}

/**
 * The first step: what this message is called, and where it can be delivered.
 * Both are decisions a marketer makes before opening the content editor, and
 * neither needs a card of its own.
 */
export const AppMessageBasicsBuilder = ({ domain, channelTargetDisabled }: AppMessageBasicsBuilderProps) => {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { details } = context

    const selectedVariant = context.variants[context.selectedVariantIdx]
    const template = selectedVariant.template!

    const onDetailsChange = (update: { name?: string; description?: string | null }) => {
        dispatch({ type: 'patch', entity: 'details', data: { ...update, domain } })
    }

    const showTemplateSelector =
        context.type === 'message' && context.mode === 'create' && context.availableTemplates.length > 0

    const isLoading = context.loading || (context.type === 'message' && !context.initialAppMessageStateLoaded)

    return (
        <Well step={1} title="Basics" className={getClassNames('app-message-basics-builder')} footerMode="hidden">
            <Skeleton loading={isLoading} active={true} avatar={false} title={false}>
                <Well.Layout direction="horizontal" showDividers={false}>
                    <Well.Section flex={1} className={getClassNames('basics-details')}>
                        <TemplatizedTextField
                            disabled={context.disabled}
                            size="middle"
                            hideMacros={true}
                            label="Name"
                            aria-label="App message name"
                            placeholder="App Message Name"
                            value={details.name}
                            limits={{ max: 100, maxConstraint: true, displayMode: 'none' }}
                            onChange={(nameVal) => onDetailsChange({ name: nameVal })}
                            formItemProps={{ layout: 'vertical' }}
                        />

                        {context.type === 'template' && (
                            <TemplatizedTextField
                                disabled={context.disabled}
                                size="middle"
                                hideMacros={true}
                                label="Description"
                                aria-label="App message description"
                                placeholder="App Message Description"
                                value={details.description}
                                limits={{ max: 255, maxConstraint: true }}
                                onChange={(descVal) => {
                                    const trimmed = descVal?.trim()
                                    onDetailsChange({ description: trimmed === '' ? undefined : trimmed })
                                }}
                                formItemProps={{ layout: 'vertical' }}
                            />
                        )}
                    </Well.Section>

                    <Well.Section title="Delivery Channels" className={getClassNames('basics-channels')}>
                        <DeliveryChannelSelector
                            nested={true}
                            type="multiple"
                            disabled={context.disabled || !!channelTargetDisabled}
                            onChange={(channels) =>
                                dispatch({
                                    type: 'patch',
                                    entity: 'template',
                                    data: template.clone({ channels }),
                                })
                            }
                            visibleChannels={getEnabledDeliveryChannels(domain).filter(
                                (channel) => channel !== DeliveryChannel.WEB,
                            )}
                            value={template.channels}
                        />
                    </Well.Section>
                </Well.Layout>

                {showTemplateSelector && (
                    <Well.Section className={getClassNames('basics-template')}>
                        <TemplateSelector
                            disabled={context.disabled}
                            domain={domain}
                            config={context}
                            loading={!context.templatesLoaded}
                            onChange={(data) => dispatch({ type: 'patch', entity: 'named-template', data })}
                            value={!context.templatesLoaded ? undefined : context.selectedTemplateId}
                        />
                    </Well.Section>
                )}
            </Skeleton>
        </Well>
    )
}
